import React, { useContext, useState } from 'react'
import { z } from 'zod'
import { OverlaySpinner, PageWrapper, TabsV2, TabsV2Item } from '../../components'
import { WemoloAccordion, WemoloButton, WemoloLoading, WemoloSearchableDropdown } from '../../components/Wemolo'
// import { MultipleDeviceMetabase } from '../DeviceConfig/MultipleDeviceMetabase'
import { LotContext } from './LotContext'
import { LotDevices } from './LotDevices'
import { LotMetabase } from './LotMetabase'
import { transformLotToMenuOption, transformSelectValueToLotType } from './utils'
import LocalErrorBoundary from '../../components/LocalErrorBoundary'
import { SENTRYTAGS } from '../../logging'
import { LotCarpayDevices } from './LotCarpayDevices'
import { OccupancyView } from '../Occupancy/OccupancyView'
import { AuditTargetType, ChangeLogView } from '../ChangeLog'
import { useFlags } from 'launchdarkly-react-client-sdk'
import { LotAccessControl } from './LotAccessControl'
import { useAccessControlStore } from '../AccessControl/AccessControlStore'
import { DetectionExplorerContainer } from '../DetectionExplorer'
import styles from './LotView.module.css'

// Persist the active lot tab across sessions. localStorage is untrusted, so reads go through a Zod
// boundary (rules/main.md).
const ACTIVE_TAB_STORAGE_KEY = 'lotView:activeTab'

const readActiveTab = (): string | null => {
    try {
        const raw = localStorage.getItem(ACTIVE_TAB_STORAGE_KEY)
        return raw ? z.string().parse(JSON.parse(raw)) : null
    } catch {
        return null
    }
}

const persistActiveTab = (key: string): void => {
    try {
        localStorage.setItem(ACTIVE_TAB_STORAGE_KEY, JSON.stringify(key))
    } catch {
        // Ignore write failures (e.g. private mode / storage disabled).
    }
}

export const LotView = () => {
    const lotContext = useContext(LotContext)
    const sites = useAccessControlStore((state) => state.sites)
    const { lpBasedAc, detectionExplorer } = useFlags()

    const accessControlSite = sites.find((site) => site.pd_lot_id === lotContext.selectedLot?.id)
    const hasAccessControl = accessControlSite && lpBasedAc

    // const deviceUUIDS = lotContext?.lotStations?.station?.map((station) => station?.device_uuid)?.filter(Boolean)

    const [activeTab, setActiveTab] = useState<string | undefined>(() => readActiveTab() ?? undefined)

    const isLoadingHardware = lotContext.loadingLots || lotContext.loadingLotStations

    const tabs: TabsV2Item[] = [
        {
            key: 'hardware',
            label: 'Hardware',
            content: isLoadingHardware ? (
                // On a cold start with `?lot=` in the URL there's no selection yet, so without this the
                // sections below read as "select a lot first" while the fetch is still in flight.
                <div className={styles.loading}>
                    <WemoloLoading />
                </div>
            ) : (
                <LocalErrorBoundary tag={SENTRYTAGS.LOT}>
                    <WemoloAccordion
                        key={`scanners-${Boolean(lotContext.selectedLot)}`}
                        title="Scanners list"
                        defaultExpanded={Boolean(lotContext.selectedLot)}
                    >
                        <LotDevices />
                    </WemoloAccordion>
                    <WemoloAccordion
                        key={`other-${Boolean(lotContext.selectedLot)}`}
                        title="Other Devices"
                        defaultExpanded={Boolean(lotContext.selectedLot)}
                    >
                        <LotCarpayDevices />
                    </WemoloAccordion>
                    <WemoloAccordion
                        key={`metabase-${Boolean(lotContext.selectedLot)}`}
                        title="Lot Metabase Dashboard"
                        defaultExpanded={Boolean(lotContext.selectedLot)}
                    >
                        <LotMetabase />
                    </WemoloAccordion>
                </LocalErrorBoundary>
            )
        },
        {
            key: 'occupancy',
            label: 'Occupancy settings',
            content: (
                <LocalErrorBoundary tag={SENTRYTAGS.DEVICECONFIG}>
                    <OverlaySpinner loadingTop={true} loading={lotContext.loading}>
                        <OccupancyView />
                    </OverlaySpinner>
                </LocalErrorBoundary>
            )
        },
        {
            key: 'changelog',
            label: 'Change log',
            content: (
                <LocalErrorBoundary tag={SENTRYTAGS.LOT}>
                    <ChangeLogView targetId={lotContext.selectedLot?.id} targetType={AuditTargetType.lot} />
                </LocalErrorBoundary>
            )
        },
        ...(detectionExplorer
            ? [
                  {
                      key: 'detection-explorer',
                      label: 'Detection Explorer',
                      content: (
                          <LocalErrorBoundary tag={SENTRYTAGS.LOT}>
                              <DetectionExplorerContainer />
                          </LocalErrorBoundary>
                      )
                  }
              ]
            : []),
        ...(hasAccessControl
            ? [
                  {
                      key: 'access-control',
                      label: 'Access Control',
                      content: (
                          <LocalErrorBoundary tag={SENTRYTAGS.LOT}>
                              <LotAccessControl site={accessControlSite} />
                          </LocalErrorBoundary>
                      )
                  }
              ]
            : [])
    ]

    // Fall back to the first tab if the persisted key isn't currently available — e.g. a flag-gated
    // tab is off, or flags haven't loaded yet (this re-resolves to the saved tab once they do).
    const activeKey = activeTab && tabs.some((tab) => tab.key === activeTab) ? activeTab : tabs[0]?.key

    return (
        <PageWrapper>
            <div className={styles.header}>
                <div className={styles.dropdownWrapper}>
                    <WemoloSearchableDropdown
                        options={lotContext.lots?.lot?.map(transformLotToMenuOption) || []}
                        value={lotContext.selectedLot?.id}
                        onChange={(id) =>
                            lotContext.setSelectedLot(transformSelectValueToLotType(id ? { id, label: id } : null))
                        }
                        placeholder="Select lot"
                    />
                </div>
                <div className={styles.spacer}></div>
                <div className={styles.actions}>
                    <WemoloButton
                        view="secondary"
                        onClick={() => {
                            const targetURL = 'https://airtable.com/appOprM6GwkxZ9CR1/pag1ODXCqjsR4quIz'

                            window.open(targetURL, '_blank')
                        }}
                    >
                        Parking Lots
                    </WemoloButton>
                    &nbsp;&nbsp;
                    <WemoloButton
                        view="secondary"
                        onClick={() => {
                            const targetURL = 'https://airtable.com/appOprM6GwkxZ9CR1/pagPf6AEVxFWi6oz5'

                            window.open(targetURL, '_blank')
                        }}
                    >
                        Station Details
                    </WemoloButton>
                    &nbsp;&nbsp;
                    <WemoloButton
                        view="secondary"
                        onClick={() => {
                            const currentURL = window.location.href
                            let targetURL

                            if (currentURL.includes('stage')) {
                                targetURL = `https://int.stage.park-depot.de/lot-dashboard/${lotContext.selectedLot?.id}`
                            } else {
                                targetURL = `https://int.park-depot.de/lot-dashboard/${lotContext.selectedLot?.id}`
                            }

                            window.open(targetURL, '_blank')
                        }}
                        disabled={!lotContext.selectedLot}
                    >
                        Go to internal
                    </WemoloButton>
                </div>
            </div>
            <TabsV2
                aria-label="Lot"
                tabs={tabs}
                activeKey={activeKey}
                onChange={(key) => {
                    setActiveTab(key)
                    persistActiveTab(key)
                }}
                renderAll
            />
        </PageWrapper>
    )
}
